import {
    CLOSE_NOTES_MODAL,
    LOAD_CONFERENCE_NOTES,
    NOTES_PANE_CLOSE,
    NOTES_PANE_OPEN,
    OPEN_NOTES_MODAL,
    SET_NOTES_SHARED_WITH,
    SET_NOTES_TEXT,
    SHARED_NOTES_DRAFT_CLEAR,
    SHARED_NOTES_DRAFT_UPDATE,
    SHARED_NOTES_EDIT_SUBMIT,
    VIEW_SHARED_NOTES
} from './actionTypes';
import { SHARE_AUDIENCE_SELECTED, SHARE_PERMISSION_VIEW } from './constants';
import { loadConferenceNotes } from './functions';

/**
 * Action to close the notes pane.
 *
 * @returns {{
 *     type: NOTES_PANE_CLOSE
 * }}
 */
export function closeNotesPane() {
    return {
        type: NOTES_PANE_CLOSE
    };
}

/**
 * Action to open the notes pane.
 *
 * @returns {{
 *     type: NOTES_PANE_OPEN
 * }}
 */
export function openNotesPane() {
    return {
        type: NOTES_PANE_OPEN
    };
}

/**
 * Displays the notes modal on native.
 *
 * @returns {{
 *     type: OPEN_NOTES_MODAL
 * }}
 */
export function openNotesModal() {
    return {
        type: OPEN_NOTES_MODAL
    };
}

/**
 * Closes the notes modal on native.
 *
 * @returns {{
 *     type: CLOSE_NOTES_MODAL
 * }}
 */
export function closeNotesModal() {
    return {
        type: CLOSE_NOTES_MODAL
    };
}

/**
 * Updates the in-memory notes text. Persistence is handled in middleware.
 *
 * @param {string} text - The notes text.
 * @param {Object|null} richText - Optional Quill document.
 * @returns {{
 *     type: SET_NOTES_TEXT,
 *     text: string
 * }}
 */
export function setNotesText(text, richText = null) {
    return {
        type: SET_NOTES_TEXT,
        text,
        richText
    };
}

/**
 * Loads private notes for a room from local storage into redux.
 *
 * @param {string|null} roomName - The conference room name.
 * @returns {{
 *     type: LOAD_CONFERENCE_NOTES,
 *     text: string
 * }}
 */
export function loadNotesFromStorage(roomName) {
    return {
        type: LOAD_CONFERENCE_NOTES,
        roomName,
        ...loadConferenceNotes(roomName)
    };
}

/**
 * Shares our notes with participants, or everyone in the room. Sending and
 * recipient normalization are handled in middleware. An empty id list with the
 * default options stops sharing and resets the share settings.
 *
 * @param {Array<string>} participantIds - Who may see our notes ('selected'
 * audience only).
 * @param {Object} [options] - Sharing options.
 * @param {string} [options.audience] - 'selected' (default) or 'everyone'.
 * @param {Object} [options.permissions] - Per-recipient 'view'|'edit' map.
 * @param {string} [options.everyonePermission] - 'view' (default) or 'edit'
 * granted to everyone in the room.
 * @returns {{
 *     type: SET_NOTES_SHARED_WITH,
 *     participantIds: Array<string>,
 *     audience: string,
 *     permissions: Object,
 *     everyonePermission: string
 * }}
 */
export function shareNotes(participantIds, options = {}) {
    const {
        audience = SHARE_AUDIENCE_SELECTED,
        permissions = {},
        everyonePermission = SHARE_PERMISSION_VIEW
    } = options;

    return {
        type: SET_NOTES_SHARED_WITH,
        participantIds,
        audience,
        permissions,
        everyonePermission
    };
}

// Correlates an edit request with the owner's reply; the timestamp keeps ids
// unique across tabs and the counter across rapid submissions.
let notesEditRequestCounter = 0;

/**
 * Submits our draft to the owner of notes shared with us for editing. The
 * owner only accepts it when the revision we based our draft on is still
 * current and our edit grant is.
 *
 * @param {string} participantId - The owner of the shared notes.
 * @param {string} text - Our edited notes text.
 * @param {Object|null} [richText] - Optional edited Quill document.
 * @param {number|null} [revision] - The shared document revision we edited.
 * @returns {{
 *     type: SHARED_NOTES_EDIT_SUBMIT,
 *     participantId: string,
 *     text: string,
 *     richText: Object|null,
 *     revision: number|null,
 *     requestId: string
 * }}
 */
export function submitSharedNotes(participantId, text, richText = null, revision = null) {
    return {
        type: SHARED_NOTES_EDIT_SUBMIT,
        participantId,
        text,
        richText,
        revision,
        requestId: `notes-edit-${Date.now()}-${++notesEditRequestCounter}`
    };
}

/**
 * Stores our in-progress edits of a participant's shared notes. Keeping them
 * in redux rather than component state means pane/tab unmounts and the two
 * responsive pane instances share one draft.
 *
 * @param {string} participantId - The owner of the shared notes.
 * @param {string} text - The draft text.
 * @param {Object|null} [richText] - Optional draft Quill document.
 * @param {number|null} [baseRevision] - The shared revision the draft is based on.
 * @returns {{
 *     type: SHARED_NOTES_DRAFT_UPDATE,
 *     participantId: string,
 *     text: string,
 *     richText: Object|null,
 *     baseRevision: number|null
 * }}
 */
export function updateSharedNotesDraft(participantId, text, richText = null, baseRevision = null) {
    return {
        type: SHARED_NOTES_DRAFT_UPDATE,
        participantId,
        text,
        richText,
        baseRevision
    };
}

/**
 * Discards our draft for a participant's shared notes and drops the resolved
 * request record, so a follow-up edit starts a fresh request.
 *
 * @param {string} participantId - The owner of the shared notes.
 * @returns {{
 *     type: SHARED_NOTES_DRAFT_CLEAR,
 *     participantId: string
 * }}
 */
export function clearSharedNotesDraft(participantId) {
    return {
        type: SHARED_NOTES_DRAFT_CLEAR,
        participantId
    };
}

/**
 * Switches the notes pane to a participant's shared notes, or back to ours.
 *
 * @param {string|null} participantId - Whose shared notes to show; null for ours.
 * @returns {{
 *     type: VIEW_SHARED_NOTES,
 *     participantId: string|null
 * }}
 */
export function viewSharedNotes(participantId) {
    return {
        type: VIEW_SHARED_NOTES,
        participantId
    };
}
